import { roundFourDecimal, roundThreeDecimal, roundTwoDecimal } from '../../calculation/util';
import { EUnitOfMeasureImperialLabel, EUnitOfMeasureLabel, EUnitOfMeasureMetricLabel } from '../../conversion/enum';
import {
    amountDrainageChimney,
    amountDrainageTrench,
    backfillVolume,
    check,
    coefficientOfActiveEarthPressure,
    coefficientOfEarthPressureAtRest,
    depthOfHorizontalStressNullificationDrained,
    depthOfHorizontalStressNullificationUndrained,
    designedSpacing,
    dischargeDisposedOfByEachPanel,
    dischargeToBeDrained,
    drainageCapacityOfASingleDrainPerMeter,
    drainingCapacityPD,
    drainingDrainPipesPD,
    drainPD,
    entireLineDrainingCapacity,
    excavationArea,
    excavationCalculationPD,
    excavationSlope,
    excavationStabilityCheck,
    excavationVolume,
    fsDrainingCapacity,
    fsSeismicMin,
    fsStaticMin,
    horizontalPressureDrainedActiveFailure,
    horizontalPressureUndrainedShortTermActiveFailure,
    hydraulicRadius,
    isPrimaryDrainageTrenchOk,
    isSecondaryDrainageTrenchOk,
    isSlopeStable,
    landfillVolume,
    maximumSpacing,
    metricCalculationDrainageChimneyPD,
    metricCalculationDrainageTrenchPD,
    nameDrainageTrench,
    phaseOneOutputPD,
    phaseTwoOutputCalculationOfPressuresInTheSoilPD,
    ratioWithDrainageCapacityQtQdren,
    sizeDrainageChimney,
    sizeDrainageTrench,
    specificDischargeToBeDrained,
    suggestedSpacing,
    timeForStabilization,
    timeForStabilizationWithChimneys,
    totalBackfillVolume,
    totalDisposalVolume,
    totalExcavationVolume,
    totalVolumesPD,
    velocity,
    verticalHeight,
    verticalPressure,
    wettedArea,
    wettedPerimeter,
    wheelbasePD,
} from '../../conversion/result';
import i18n from '../../localization';
import { ITable, ITableRow } from '../../shared/component/Table';
import { ECurrency, EDrainageTrenchType, EExcavationStabilityCheck, EStableSlopeValidationType, EUnitOfMeasure, EWheelbaseCheck } from '../../shared/enum';
import {
    IDrain,
    IDrainageSystem,
    IExcavationCalculation,
    IMetricCalculationOutput,
    IPhaseTwoOutputCalculationOfPressuresInTheSoil,
    IPhaseTwoOutputDrainingCapacity,
    IPhaseTwoOutputDrainingDrainPipes,
    IPhaseTwoOutputTransitionalRegime,
    IPhaseTwoOutputWheelbase,
    ITimeForStabilization,
    ITotalVolumes,
    TDrain,
    TDrainageSystem,
    TDrainingCapacity,
    TDrainingDrainPipes,
    TExcavationCalculation,
    TMetricCalculationDrainageChimney,
    TMetricCalculationDrainageTrench,
    TPhaseOneOutput,
    TPhaseTwoOutputCalculationOfPressuresInTheSoil,
    TTotalVolumes,
    TTransitionalRegime,
    TWheelbase,
} from '../../shared/iResult';
import { redux } from '../../store';
import {
    currency,
    IPrice,
    primaryChimney,
    primaryChimneyPrice,
    primaryCollector,
    primaryDrainageTrenchPrice,
    secondaryChimney,
    secondaryChimneyPrice,
    secondaryCollector,
    secondaryDrainageTrenchPrice,
    total,
    TPrice,
} from '../../store/price/priceSlice';

const createColumns = (initialValue: string[], condition?: boolean, addElement?: string) => (condition ? [...initialValue, addElement!] : initialValue);

const getDrainageCapacityOutput = (name: string, value: number | boolean) => {
    let a = value;
    if (typeof value == 'number')
        switch (name) {
            case drainageCapacityOfASingleDrainPerMeter:
                a = (value < 0.0001 ? value.toExponential(2) : roundFourDecimal(value)) as number;
                break;
            case entireLineDrainingCapacity:
                a = (value < 0.0001 ? value.toExponential(2) : roundThreeDecimal(value)) as number;
                break;
            case dischargeToBeDrained:
                a = (value < 0.0001 ? value.toExponential(2) : roundThreeDecimal(value)) as number;
                break;
            case specificDischargeToBeDrained:
                a = (value < 0.0001 ? value.toExponential(2) : roundFourDecimal(value)) as number;
                break;
            case fsDrainingCapacity:
                a = roundTwoDecimal(value) as number;
                break;
        }
    return a;
};

export const getDrainageCapacityTable = (drainageCapacity: IPhaseTwoOutputDrainingCapacity): ITable => {
    const project = redux.getState().project;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    let columns = createColumns(['', i18n.t('primaryCollector')], hasSecondaryDrainageTrench, i18n.t('secondaryCollector'));
    let output = [drainageCapacityOfASingleDrainPerMeter, entireLineDrainingCapacity, dischargeToBeDrained, specificDischargeToBeDrained, fsDrainingCapacity];
    let rows = output.map(x => {
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: drainingCapacityPD[x as TDrainingCapacity].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value: getDrainageCapacityOutput(x, drainageCapacity.primaryDrainageTrench[x as TDrainingCapacity]!),
                    check: x == fsDrainingCapacity ? drainageCapacity.primaryDrainageTrench.isOkDrainingCapacity : undefined,
                },
            ],
        };
        if (hasSecondaryDrainageTrench) {
            row.values.push({
                value: getDrainageCapacityOutput(x, drainageCapacity.secondaryDrainageTrench![x as TDrainingCapacity]!),
                check: x == fsDrainingCapacity ? drainageCapacity.secondaryDrainageTrench!.isOkDrainingCapacity : undefined,
            });
        }
        return row;
    });
    return { columns, rows };
};

export const getDrainingDrainPipesTable = (drainingDrainPipes: IPhaseTwoOutputDrainingDrainPipes): ITable | undefined => {
    const project = redux.getState().project;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;

    const hasPrimaryDrainPipe = project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench]?.product?.hasPipe;
    const hasSecondaryDrainPipe = project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench]?.product?.hasPipe;

    if (!hasPrimaryDrainPipe && !hasSecondaryDrainPipe) {
        return undefined;
    }

    let columns = createColumns(['', i18n.t('primaryCollector')], hasSecondaryDrainageTrench, i18n.t('secondaryCollector'));
    let output = [wettedArea, wettedPerimeter, hydraulicRadius, velocity, dischargeDisposedOfByEachPanel, ratioWithDrainageCapacityQtQdren];
    let rows = output.map(x => {
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: drainingDrainPipesPD[x as TDrainingDrainPipes].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value: hasPrimaryDrainPipe ? drainingDrainPipes.primaryDrainageTrench[x as TDrainingDrainPipes]! : '-',
                    check: hasPrimaryDrainPipe && x == ratioWithDrainageCapacityQtQdren ? drainingDrainPipes.primaryDrainageTrench.isOkRatioWithDrainageCapacityQtQdren : undefined,
                },
            ],
        };
        if (hasSecondaryDrainageTrench) {
            row.values.push({
                value: hasSecondaryDrainPipe ? drainingDrainPipes.secondaryDrainageTrench![x as TDrainingDrainPipes]! : '-',
                check: hasSecondaryDrainPipe && x == ratioWithDrainageCapacityQtQdren ? drainingDrainPipes.secondaryDrainageTrench!.isOkRatioWithDrainageCapacityQtQdren : undefined,
            });
        }
        return row;
    });
    if (hasSecondaryDrainageTrench) {
        rows.push({
            label: i18n.t('maximumNumberOfSecondaryDrainingLines'),
            values: [
                {
                    value: hasPrimaryDrainPipe ? (drainingDrainPipes.maximumNumberOfSecondaryDrainingLines < 0 ? 0 : drainingDrainPipes.maximumNumberOfSecondaryDrainingLines) : '-',
                    check: hasPrimaryDrainPipe ? drainingDrainPipes.isOkMaximumNumberOfSecondaryDrainingLines : undefined,
                },
                {
                    value: '-',
                },
            ],
        });
    }

    return { columns, rows };
};

const wheelbaseCheckLabel = {
    [EWheelbaseCheck.Ok]: true,
    [EWheelbaseCheck.DependentOnSecondaryDrainageTrenches]: i18n.t('dependentOnSecondaryCollectors'),
    [EWheelbaseCheck.NotOk]: false,
};

export const getWheelBaseTable = (wheelbase: IPhaseTwoOutputWheelbase, isReport?: boolean): ITable => {
    const project = redux.getState().project;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    let columns = createColumns(['', i18n.t('primaryCollector')], hasSecondaryDrainageTrench, i18n.t('secondaryCollector'));
    let output = [designedSpacing, maximumSpacing, check];
    if (!isReport) output.push(suggestedSpacing);
    wheelbase.primaryDrainageTrench.designedSpacing = project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].wheelbase;
    let rows = output.map(x => {
        let valuePrimary = x == check ? wheelbaseCheckLabel[wheelbase.primaryDrainageTrench[check]! as EWheelbaseCheck] : wheelbase.primaryDrainageTrench[x as TWheelbase]!;
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: wheelbasePD[x as TWheelbase].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value: valuePrimary,
                },
            ],
        };
        if (hasSecondaryDrainageTrench) {
            wheelbase.secondaryDrainageTrench.designedSpacing = project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench].wheelbase;
            let valueSecondary = x == check ? wheelbaseCheckLabel[wheelbase.secondaryDrainageTrench[check]! as EWheelbaseCheck] : wheelbase.secondaryDrainageTrench[x as TWheelbase]!;
            row.values.push({
                value: valueSecondary,
            });
        }
        return row;
    });
    return { columns, rows };
};

const handleTransitionalRegimeTable = (key: string, hasDrainageChimney: boolean, value: any) => {
    if (key !== timeForStabilizationWithChimneys) return value;
    return hasDrainageChimney ? value : '-';
};

export const getTransitionalRegimeTable = (transitionalRegime: IPhaseTwoOutputTransitionalRegime): ITable => {
    const project = redux.getState().project;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    let columns = createColumns(['', i18n.t('primaryCollector')], hasSecondaryDrainageTrench, i18n.t('secondaryCollector'));
    let output = [timeForStabilization];
    const hasPrimaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].hasDrainageChimney!;
    const hasSecondaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench]?.hasDrainageChimney!;
    if (hasPrimaryDrainageChimney || hasSecondaryDrainageChimney) output.push(timeForStabilizationWithChimneys);
    let rows = output.map(x => {
        let row: ITableRow = {
            label: i18n.t(x),
            values: [
                {
                    value: handleTransitionalRegimeTable(
                        x,
                        hasPrimaryDrainageChimney,
                        `${(transitionalRegime.primaryDrainageTrench[x as TTransitionalRegime] as ITimeForStabilization).hours} ${EUnitOfMeasureLabel.hours} (${
                            (transitionalRegime.primaryDrainageTrench[x as TTransitionalRegime] as ITimeForStabilization).days
                        } ${EUnitOfMeasureLabel.days})`,
                    ),
                },
            ],
        };
        if (hasSecondaryDrainageTrench) {
            row.values.push({
                value: handleTransitionalRegimeTable(
                    x,
                    hasSecondaryDrainageChimney,
                    `${(transitionalRegime.secondaryDrainageTrench[x as TTransitionalRegime] as ITimeForStabilization).hours} ${EUnitOfMeasureLabel.hours} (${
                        (transitionalRegime.secondaryDrainageTrench[x as TTransitionalRegime] as ITimeForStabilization).days
                    } ${EUnitOfMeasureLabel.days})`,
                ),
            });
        }
        return row;
    });
    return { columns, rows };
};

const timeForSystemStabilization = 'timeForSystemStabilization';
const timeForSystemStabilizationWithChimneys = 'timeForSystemStabilizationWithChimneys';

const handleDrainageSystemTable = (key: string, hasPrimaryDrainageChimney: boolean, hasSecondaryDrainageChimney: boolean, transitionalRegime: IPhaseTwoOutputTransitionalRegime, value: any) => {
    if (key !== timeForSystemStabilizationWithChimneys) return value;
    if (hasPrimaryDrainageChimney && hasSecondaryDrainageChimney) return value;
    if (hasPrimaryDrainageChimney && !hasSecondaryDrainageChimney)
        return `${(transitionalRegime.primaryDrainageTrench[timeForStabilizationWithChimneys] as ITimeForStabilization).hours} ${EUnitOfMeasureLabel.hours} (${
            (transitionalRegime.primaryDrainageTrench[timeForStabilizationWithChimneys] as ITimeForStabilization).days
        } ${EUnitOfMeasureLabel.days})`;
    if (!hasPrimaryDrainageChimney && hasSecondaryDrainageChimney)
        return `${(transitionalRegime.secondaryDrainageTrench[timeForStabilizationWithChimneys] as ITimeForStabilization).hours} ${EUnitOfMeasureLabel.hours} (${
            (transitionalRegime.secondaryDrainageTrench[timeForStabilizationWithChimneys] as ITimeForStabilization).days
        } ${EUnitOfMeasureLabel.days})`;
};

export const getDrainageSystemTable = (drainageSystem: IDrainageSystem, transitionalRegime: IPhaseTwoOutputTransitionalRegime): ITable => {
    const project = redux.getState().project;
    let columns = createColumns([i18n.t('drainageSystem'), i18n.t('time')]);
    let output = [timeForSystemStabilization];
    const hasPrimaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].hasDrainageChimney!;
    const hasSecondaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench]?.hasDrainageChimney!;
    if (hasPrimaryDrainageChimney || hasSecondaryDrainageChimney) output.push(timeForSystemStabilizationWithChimneys);
    let rows = output.map(x => {
        let row: ITableRow = {
            label: i18n.t(x),
            values: [
                {
                    value: handleDrainageSystemTable(
                        x,
                        hasPrimaryDrainageChimney,
                        hasSecondaryDrainageChimney,
                        transitionalRegime,
                        `${drainageSystem[x as TDrainageSystem].hours} ${EUnitOfMeasureLabel.hours} (${drainageSystem[x as TDrainageSystem].days} ${EUnitOfMeasureLabel.days})`,
                    ),
                },
            ],
        };
        return row;
    });
    return { columns, rows };
};

export const getGeneralPressuresInTheSoilTable = (calculationOfPressuresInTheSoil: IPhaseTwoOutputCalculationOfPressuresInTheSoil): ITable => {
    const project = redux.getState().project;
    let columns = createColumns(['', '']);
    let output = [coefficientOfEarthPressureAtRest, coefficientOfActiveEarthPressure, verticalPressure];
    let rows = output.map(x => {
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: phaseTwoOutputCalculationOfPressuresInTheSoilPD[x as TPhaseTwoOutputCalculationOfPressuresInTheSoil].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value: calculationOfPressuresInTheSoil[x as TPhaseTwoOutputCalculationOfPressuresInTheSoil]! as number,
                },
            ],
        };
        return row;
    });

    return { columns, rows };
};

export const getLongTermPressuresInTheSoilTable = (longTerm: IDrain): ITable => {
    const project = redux.getState().project;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    let columns = createColumns([i18n.t('longTermVerify'), '']);
    let output = [horizontalPressureDrainedActiveFailure, isPrimaryDrainageTrenchOk, isSecondaryDrainageTrenchOk, depthOfHorizontalStressNullificationDrained].filter(
        x => x != isSecondaryDrainageTrenchOk || (x == isSecondaryDrainageTrenchOk && hasSecondaryDrainageTrench),
    );
    let rows = output.map(x => {
        let value = longTerm[x as TDrain]!;
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: drainPD[x as TDrain].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value,
                },
            ],
        };
        return row;
    });

    return { columns, rows };
};

export const getShortTermPressuresInTheSoilTable = (shortTerm: IDrain): ITable => {
    const project = redux.getState().project;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    let columns = createColumns([i18n.t('shortTermVerify'), '']);
    let output = [horizontalPressureUndrainedShortTermActiveFailure, isPrimaryDrainageTrenchOk, isSecondaryDrainageTrenchOk, depthOfHorizontalStressNullificationUndrained].filter(
        x => x != isSecondaryDrainageTrenchOk || (x == isSecondaryDrainageTrenchOk && hasSecondaryDrainageTrench),
    );
    let rows = output.map(x => {
        let value = shortTerm[x as TDrain]!;
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: drainPD[x as TDrain].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value,
                },
            ],
        };

        return row;
    });

    return { columns, rows };
};

export const getInterventionSlopeValidationTable = (_stableSlope: any, type: EStableSlopeValidationType): ITable => {
    const project = redux.getState().project;
    let columns = createColumns([type == EStableSlopeValidationType.Pre ? i18n.t('preInterventionSlopeValidation') : i18n.t('postInterventionSlopeValidation'), '']);
    let output = [isSlopeStable, fsStaticMin];

    if (project.data?.load?.hasSeismicLoads) {
        output.push(fsSeismicMin);
    }

    let rows = output.map(x => {
        let value = _stableSlope[x as any]! as number | boolean;
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: phaseOneOutputPD[x as TPhaseOneOutput].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value,
                },
            ],
        };

        return row;
    });

    return { columns, rows };
};

export const getMetricCalculationAmountTable = (metricCalculationOutput: IMetricCalculationOutput): ITable => {
    const project = redux.getState().project;
    const util = redux.getState().util;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    const hasPrimaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench]?.hasDrainageChimney;
    const hasSecondaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench]?.hasDrainageChimney;
    let columns = createColumns([i18n.t('numberOfDrains'), '']);
    let outputDrainageTrench = [amountDrainageTrench];
    let outputDrainageChimney = [amountDrainageChimney];
    let rows: ITableRow[] = [];

    let unitOfMeasureSize = project.data?.projectInfo.unitOfMeasure == EUnitOfMeasure.Metric ? EUnitOfMeasureMetricLabel.meter : EUnitOfMeasureImperialLabel.foot;

    outputDrainageTrench.map(x => {
        let row: ITableRow = {
            label: i18n.t('amountPrimaryDrainageTrench', {
                unitOfMeasure: metricCalculationDrainageTrenchPD[x as TMetricCalculationDrainageTrench].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                name: metricCalculationOutput.primaryDrainageTrench[nameDrainageTrench],
                size: `${metricCalculationOutput.primaryDrainageTrench[sizeDrainageTrench]} ${unitOfMeasureSize}`,
            }),
            values: [
                {
                    value: metricCalculationOutput.primaryDrainageTrench[x as TMetricCalculationDrainageTrench],
                },
            ],
        };
        rows.push(row);
        if (hasSecondaryDrainageTrench) {
            let row: ITableRow = {
                label: i18n.t('amountSecondaryDrainageTrench', {
                    unitOfMeasure: metricCalculationDrainageTrenchPD[x as TMetricCalculationDrainageTrench].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                    name: metricCalculationOutput.secondaryDrainageTrench[nameDrainageTrench],
                    size: `${metricCalculationOutput.secondaryDrainageTrench[sizeDrainageTrench]} ${unitOfMeasureSize}`,
                }),
                values: [
                    {
                        value: util.data?.drainSymmetric
                            ? (metricCalculationOutput.secondaryDrainageTrench[x as TMetricCalculationDrainageTrench] as number) * 2 +
                              '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0' +
                              i18n.t('drainSymmetric') +
                              ' ' +
                              i18n.t('yes')
                            : metricCalculationOutput.secondaryDrainageTrench[x as TMetricCalculationDrainageTrench] +
                              '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0' +
                              i18n.t('drainSymmetric') +
                              ' ' +
                              i18n.t('no'),
                    },
                ],
            };
            rows.push(row);
        }
    });
    if (hasPrimaryDrainageChimney) {
        outputDrainageChimney.map(y => {
            let row: ITableRow = {
                label: i18n.t('amountPrimaryDrainageChimney', {
                    unitOfMeasure: metricCalculationDrainageChimneyPD[y as TMetricCalculationDrainageChimney].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                    size: `${metricCalculationOutput.primaryDrainageChimney[sizeDrainageChimney]} ${unitOfMeasureSize}`,
                }),
                values: [
                    {
                        value: metricCalculationOutput.primaryDrainageChimney[y as TMetricCalculationDrainageChimney],
                    },
                ],
            };
            rows.push(row);
            if (hasSecondaryDrainageChimney) {
                let row: ITableRow = {
                    label: i18n.t('amountSecondaryDrainageChimney', {
                        unitOfMeasure: metricCalculationDrainageChimneyPD[y as TMetricCalculationDrainageChimney].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                        size: `${metricCalculationOutput.secondaryDrainageChimney[sizeDrainageChimney]} ${unitOfMeasureSize}`,
                    }),
                    values: [
                        {
                            value: metricCalculationOutput.secondaryDrainageChimney[y as TMetricCalculationDrainageChimney],
                        },
                    ],
                };
                rows.push(row);
            }
        });
    }
    return { columns, rows };
};

export const getMetricCalculationCommonTable = (metricCalculationOutput: IMetricCalculationOutput): ITable => {
    const project = redux.getState().project;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    let columns = createColumns(['', i18n.t('primaryCollector')], hasSecondaryDrainageTrench, i18n.t('secondaryCollector'));
    let outputDrainageTrench = [excavationArea, excavationVolume, backfillVolume, landfillVolume];
    let outputDrainageChimney = [amountDrainageChimney];
    let rows: ITableRow[] = [];

    let unitOfMeasureSize = project.data?.projectInfo.unitOfMeasure == EUnitOfMeasure.Metric ? EUnitOfMeasureMetricLabel.meter : EUnitOfMeasureImperialLabel.foot;

    outputDrainageTrench.map(x => {
        let label = i18n.t(x, {
            unitOfMeasure: metricCalculationDrainageTrenchPD[x as TMetricCalculationDrainageTrench].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
        });
        if (x == amountDrainageTrench) {
            label = i18n.t('amountDrainageTrench', {
                unitOfMeasure: metricCalculationDrainageTrenchPD[x as TMetricCalculationDrainageTrench].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                name: metricCalculationOutput.primaryDrainageTrench[nameDrainageTrench],
                size: `${metricCalculationOutput.primaryDrainageTrench[sizeDrainageTrench]} ${unitOfMeasureSize}`,
            });
        }
        let row: ITableRow = {
            label,
            values: [
                {
                    value: metricCalculationOutput.primaryDrainageTrench[x as TMetricCalculationDrainageTrench],
                },
            ],
        };
        if (hasSecondaryDrainageTrench) {
            row.values.push({
                value: metricCalculationOutput.secondaryDrainageTrench[x as TMetricCalculationDrainageTrench],
            });
        }
        rows.push(row);
        if (x == amountDrainageTrench) {
            outputDrainageChimney.map(y => {
                let label = i18n.t(y, {
                    unitOfMeasure: metricCalculationDrainageChimneyPD[y as TMetricCalculationDrainageChimney].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                });
                if (x == amountDrainageChimney) {
                    label = i18n.t('amountDrainageChimney', {
                        unitOfMeasure: metricCalculationDrainageChimneyPD[y as TMetricCalculationDrainageChimney].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                        size: `${metricCalculationOutput.primaryDrainageChimney[sizeDrainageChimney]} ${unitOfMeasureSize}`,
                    });
                }
                let row: ITableRow = {
                    label,
                    values: [
                        {
                            value: metricCalculationOutput.primaryDrainageChimney[y as TMetricCalculationDrainageChimney],
                        },
                    ],
                };
                if (hasSecondaryDrainageTrench) {
                    row.values.push({
                        value: metricCalculationOutput.secondaryDrainageChimney[y as TMetricCalculationDrainageChimney],
                    });
                }
                rows.push(row);
            });
        }
    });
    return { columns, rows };
};

const excavationCalculationLabel = {
    [EExcavationStabilityCheck.VerticalExcavationReinforcementRequired]: i18n.t('VerticalExcavationReinforcementRequired'),
    [EExcavationStabilityCheck.SelfSupportedExcavation]: i18n.t('SelfSupportedExcavation'),
};

export const getMetricCalculationExcavationTable = (excavationCalculation: IExcavationCalculation): ITable => {
    const project = redux.getState().project;
    let columns = createColumns([i18n.t('excavationCalculation'), '']);
    let output = [excavationSlope, verticalHeight, excavationStabilityCheck];
    let rows = output.map(x => {
        let value: number | string = excavationCalculation[x as TExcavationCalculation];
        if (x == excavationStabilityCheck) value = excavationCalculationLabel[excavationCalculation[x as TExcavationCalculation] as EExcavationStabilityCheck];
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: excavationCalculationPD[x as TExcavationCalculation].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value,
                },
            ],
        };

        return row;
    });

    return { columns, rows };
};

export const getMetricCalculationTotalVolumesTable = (totalVolumes: ITotalVolumes): ITable => {
    const project = redux.getState().project;
    let columns = createColumns([i18n.t('totalVolumes'), '']);
    let output = [totalExcavationVolume, totalBackfillVolume, totalDisposalVolume];
    let rows = output.map(x => {
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: totalVolumesPD[x as TTotalVolumes].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value: totalVolumes[x as TTotalVolumes],
                },
            ],
        };

        return row;
    });

    return { columns, rows };
};

export const getMetricCalculationPriceInputTable = (price: IPrice, metricCalculationOutput: IMetricCalculationOutput): ITable => {
    const project = redux.getState().project;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    const hasPrimaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench]?.hasDrainageChimney;
    const hasSecondaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench]?.hasDrainageChimney;
    let columns = createColumns([i18n.t('inputPrice'), '']);
    let input = [primaryDrainageTrenchPrice];
    if (hasSecondaryDrainageTrench) input.push(secondaryDrainageTrenchPrice);
    if (hasPrimaryDrainageChimney) input.push(primaryChimneyPrice);
    if (hasSecondaryDrainageTrench && hasSecondaryDrainageChimney) input.push(secondaryChimneyPrice);
    let rows = input.map(x => {
        let label = i18n.t(x);
        if (x == primaryDrainageTrenchPrice) label = i18n.t(x, { name: metricCalculationOutput.primaryDrainageTrench.name });
        if (x == secondaryDrainageTrenchPrice) label = i18n.t(x, { name: metricCalculationOutput.secondaryDrainageTrench.name });
        let row: ITableRow = {
            label,
            values: [
                {
                    value: price[x as TPrice] + ' ' + (price[currency] == ECurrency.Euro ? '€' : '$'),
                },
            ],
        };

        return row;
    });

    return { columns, rows };
};

export const getMetricCalculationPriceOutputTable = (price: IPrice, metricCalculationOutput: IMetricCalculationOutput): ITable => {
    const project = redux.getState().project;
    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    const hasPrimaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench]?.hasDrainageChimney;
    const hasSecondaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench]?.hasDrainageChimney;
    let columns = createColumns([i18n.t('outputPrice'), '']);
    let output = [primaryCollector];
    if (hasSecondaryDrainageTrench) output.push(secondaryCollector);
    if (hasPrimaryDrainageChimney) output.push(primaryChimney);
    if (hasSecondaryDrainageTrench && hasSecondaryDrainageChimney) output.push(secondaryChimney);
    output.push(total);
    let rows = output.map(x => {
        let row: ITableRow = {
            label: i18n.t(x),
            values: [
                {
                    value: price[x as TPrice] + ' ' + (price[currency] == ECurrency.Euro ? '€' : '$'),
                },
            ],
        };

        return row;
    });

    return { columns, rows };
};
